<template>
  <div>
    <div class="d-flex align-start justify-space-between">
      <div class="d-flex flex-wrap">
        <div v-if="isRegularUser" class="me-2 d-flex align-center mb-4">
          <v-btn
            v-for="(filter, index) in regularUserFilters"
            elevation="0"
            rounded
            small
            :class="index < regularUserFilters.length ? 'me-2' : ''"
            :color="
              activeFilter === filter.value ? 'primary' : 'blue-grey lighten-2'
            "
            :key="filter.id"
            :outlined="activeFilter !== filter.value"
            @click="handleChipFilterChange(filter.value)"
          >
            <span
              :class="
                activeFilter === filter.value
                  ? 'white--text'
                  : 'blue-grey--text text--darken-2'
              "
            >
              {{ filter.text }}
            </span>
          </v-btn>
        </div>

        <div
          class="align-center"
          :class="{ 'd-flex flex-wrap': isOrganizationAdminOrReverAdmin }"
        >
          <Filters
            hideSaveButton
            enableAdditionalFilters
            :customNames="customFilterNames"
            :enabledFilters="availableFiltersByRole"
            :initialFilters="initialFilters"
            @change="handleGeneralFiltersChange($event)"
            @apply="applyFilters"
          >
            <template slot="prepend">
              <div v-if="showFilterSelector">
                <div
                  v-if="isRoutineManagerOrSiteAdmin"
                  class="d-flex flex-wrap me-6 align-center mb-3"
                >
                  <SelectorFilter
                    emitAutoApplyMetadata
                    id="filter_by_site_admin_routine_manager"
                    :label="$t('filters.filter_by')"
                    :options="routineManagerFilters"
                    :value="activeFilter"
                    @change="handleMainSelectorFilterChange($event)"
                  />
                  <SelectorFilter
                    v-if="activeSitePickerConfig"
                    allowClear
                    class="ms-6"
                    :data-testid="activeSitePickerConfig.testId"
                    emitAutoApplyMetadata
                    multiselect
                    :id="activeSitePickerConfig.id"
                    :label="$t('routines.select_site')"
                    :options="siteOptions"
                    :value="activeSitePickerConfig.value"
                    @change="handleAdminFilterChange($event)"
                  />
                </div>
                <div
                  v-if="isOrganizationAdminOrReverAdmin"
                  class="d-flex me-6 me-2 align-center mb-3"
                >
                  <SelectorFilter
                    emitAutoApplyMetadata
                    id="filter_by"
                    :label="$t('filters.filter_by')"
                    :options="adminFilters"
                    :value="adminActiveFilter"
                    @change="handleMainSelectorFilterChange($event)"
                  />
                  <SelectorFilter
                    v-if="adminActiveFilter === ADMIN_FILTERS.myRoutines"
                    emitAutoApplyMetadata
                    class="ms-6"
                    :label="$t('routines.my_routines')"
                    :id="ADMIN_FILTERS.myRoutines"
                    :options="adminMyRoutineFilters"
                    :value="activeFilter"
                    @change="handleMyRoutineFilterChange($event)"
                  />
                  <AssigneesSelectorFilter
                    v-if="adminActiveFilter === ADMIN_FILTERS.assignees"
                    class="ms-6"
                    emitAutoApplyMetadata
                    multiple
                    :availableAccounts="accounts"
                    :selectedAccountIds="selectedAssignees"
                    :label="$t('common.assignee_other')"
                    :isLoading="isSearchingAccounts"
                    includeCurrentAccountAtTop
                    @clear="handleClearAssignees"
                    @search="handleSearchAccounts"
                    @change="handleAdminAssigneeFilterChange($event)"
                  />
                  <SelectorFilter
                    v-if="adminActiveFilter === REGULAR_USER_FILTERS.forMyTeams"
                    emitAutoApplyMetadata
                    class="ms-6"
                    allowClear
                    multiselect
                    :label="$t('routines.select_team')"
                    :id="REGULAR_USER_FILTERS.forMyTeams"
                    :options="teamOptions"
                    :value="selectedTeams"
                    @change="handleAdminFilterChange($event)"
                  />
                  <SelectorFilter
                    v-if="adminActiveFilter === REGULAR_USER_FILTERS.forMySite"
                    emitAutoApplyMetadata
                    class="ms-6"
                    allowClear
                    multiselect
                    :label="$t('routines.select_site')"
                    :id="REGULAR_USER_FILTERS.forMySite"
                    :options="siteOptions"
                    :value="selectedAssignedSites"
                    @change="handleAdminFilterChange($event)"
                  />
                  <SelectorFilter
                    v-if="adminActiveFilter === ADMIN_FILTERS.createdIn"
                    emitAutoApplyMetadata
                    class="ms-6"
                    allowClear
                    multiselect
                    :label="$t('routines.select_site')"
                    :id="ADMIN_FILTERS.createdIn"
                    :options="siteOptions"
                    :value="selectedCreationSites"
                    @change="handleAdminFilterChange($event)"
                  />
                  <SelectorFilter
                    v-if="
                      adminActiveFilter ===
                      ROUTINE_MANAGER_FILTERS.executableInSite
                    "
                    emitAutoApplyMetadata
                    class="ms-6"
                    allowClear
                    multiselect
                    :label="$t('routines.select_site')"
                    :id="ROUTINE_MANAGER_FILTERS.executableInSite"
                    :options="siteOptions"
                    :value="selectedExecutableSites"
                    @change="handleAdminFilterChange($event)"
                  />
                </div>
              </div>
            </template>
          </Filters>
        </div>
      </div>

      <div class="d-flex align-center">
        <v-btn
          class="me-2"
          color="blue"
          rounded
          small
          text
          @click="
            isOrganizationAdminOrReverAdmin
              ? clearOrgAdminFilters
              : clearFilters
          "
        >
          {{
            isOrganizationAdminOrReverAdmin
              ? $t('filters.reset_filters')
              : $t('routines.clear_filters')
          }}
        </v-btn>

        <div class="d-flex align-end">
          <Menu :options="viewOptions" :selectedOption="viewType">
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                text
                color="white"
                class="ms-0 px-0 me-5"
                data-testid="explore-button-toggleView"
                data-tourid="explore-button-toggleView"
                :disabled="!totalItems || loading"
                :style="totalItems && !loading ? '' : 'opacity : 0.5'"
              >
                <v-icon color="blue-grey darken-1">mdi-eye-outline</v-icon>
                <span class="pl-3 caption blue-grey--text text--darken-1">{{
                  $t('common.view')
                }}</span>

                <v-icon color="blue-grey darken-1" right>mdi-menu-down</v-icon>
              </v-btn>
            </template>
          </Menu>

          <Menu
            :closeOnContentClick="false"
            :options="sortOptions"
            :selectedOption="selectedSort"
          >
            <template v-slot:activator="{ on }">
              <v-btn v-on="on" text color="white" class="mx-0 px-0 me-4">
                <v-icon color="blue-grey darken-1" left>mdi-sort</v-icon>

                <span class="blue-grey--text text--darken-1 caption">
                  {{ $t('directory.sort') }}
                </span>

                <v-icon color="blue-grey darken-1" right>mdi-menu-down</v-icon>
              </v-btn>
            </template>
          </Menu>
        </div>
      </div>
    </div>

    <div v-if="showCreatedInForeignSiteAlert" class="d-inline-block mt-3">
      <Alert
        data-testid="routineFilters-alert-createdInForeignSite"
        :text="$t('routines.created_in_foreign_site_alert')"
        :vuetifyProps="{ color: 'warning', icon: 'mdi-alert-outline' }"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'
import { utils } from 'frontend-js-shared'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import trackingMixin from '@/mixins/tracking/trackerMixin.vue'
import getEntityId from '@/lib/getEntityId'

import Menu from '@/components/UI/Menu.vue'
import Alert from '@/components/Alert.vue'
import Filters from '@/components/Filters/Filters.vue'
import SelectorFilter from '@/components/Filters/SelectorFilter.vue'
import AssigneesSelectorFilter from '@/components/AssigneesSelector/AssigneesSelectorFilter.vue'
import { buildSiteOptions } from '@/components/Filters/buildSiteOptions'

const REGULAR_USER_FILTERS = {
  forMyTeams: 'assignedTeams',
  forMySite: 'assignedSites',
  isGloballyAssigned: 'isGloballyAssigned',
}

const ADMIN_FILTERS = {
  toDo: 'isExecutableByMe',
  myRoutines: 'myRoutines',
  assignees: 'assignees',
  createdIn: 'sites',
  all: 'all',
}

const ROUTINE_MANAGER_FILTERS = {
  collaborators: 'collaborators',
  createdBy: 'createdBy',
  executableInSite: 'executableInSite',
}

const SORT_OPTIONS = {
  coincidence: '_score-desc',
  newestFirst: 'createdAt-desc',
  oldestFirst: 'createdAt-asc',
  nameAsc: 'name-asc',
  nameDesc: 'name-desc',
}

const VIEW_OPTIONS = {
  cards: 'cards',
  table: 'table',
}

export default {
  name: 'RoutineDefinitionsFilters',

  mixins: [mixin, trackingMixin],

  components: {
    Filters,
    SelectorFilter,
    AssigneesSelectorFilter,
    Menu,
    Alert,
  },

  props: {
    searchTerms: {
      type: String,
      default: '',
    },
    totalItems: {
      type: Number,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },

    viewType: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      accounts: [],
      ADMIN_FILTERS,
      ROUTINE_MANAGER_FILTERS,
      REGULAR_USER_FILTERS,
      adminActiveFilter: ADMIN_FILTERS.myRoutines,
      availableTeams: [],
      customFilterNames: {
        sites: this.$t('routines.created_in'),
      },
      filterBySelection: {},
      filtersInitialized: false,
      generalFilters: {},
      initialFilters: {},
      isSearchingAccounts: false,
      isSearchingTeams: false,
      activeFilter: ADMIN_FILTERS.assignees,
      selectedFilters: {},
      selectedSort: this.searchTerms
        ? SORT_OPTIONS.coincidence
        : SORT_OPTIONS.newestFirst,
    }
  },

  mounted() {
    const regularUserFilterKey = Object.keys(this.$route.query).find(key =>
      Object.values(REGULAR_USER_FILTERS).includes(key)
    )
    const routineManagerFilterKey = Object.keys(this.$route.query).find(key =>
      Object.values(ROUTINE_MANAGER_FILTERS).includes(key)
    )

    const adminFilterKey = Object.keys(this.$route.query).find(key =>
      Object.values(ADMIN_FILTERS).includes(key)
    )

    this.activeFilter =
      routineManagerFilterKey ?? regularUserFilterKey ?? ADMIN_FILTERS.assignees

    if (adminFilterKey) {
      this.activeFilter = adminFilterKey
      this.adminActiveFilter = adminFilterKey
    }

    this.initFilters()
  },

  watch: {
    searchTerms(terms) {
      this.selectedSort = terms
        ? SORT_OPTIONS.coincidence
        : SORT_OPTIONS.newestFirst
    },
  },

  computed: {
    ...mapGetters([
      'account',
      'accountId',
      'authorizedSiteDetails',
      'organizationId',
      'organizationSites',
    ]),

    showFilterSelector() {
      return (
        this.isOrganizationAdminOrReverAdmin || this.isRoutineManagerOrSiteAdmin
      )
    },

    isCreatedInFilterForSiteAdminsEnabled() {
      return this.$isFeatureEnabled(
        this.$featuresKeys.createdInFilterForSiteAdminsOnRoutines
      )
    },

    hasMultiSiteScope() {
      return (
        Boolean(this.account?.isSiteAdmin) &&
        this.authorizedSiteDetails.length > 1
      )
    },

    defaultSiteFilterValue() {
      return [getEntityId(this.account.site)]
    },

    hasSelectedForeignCreationSite() {
      const accountSiteId = utils.getEntityId(this.account?.site)
      if (!accountSiteId) return false
      return this.selectedCreationSites.some(
        siteId => siteId !== accountSiteId
      )
    },

    showCreatedInForeignSiteAlert() {
      return (
        this.isRoutineManagerOrSiteAdmin &&
        this.isCreatedInFilterForSiteAdminsEnabled &&
        this.activeFilter === ADMIN_FILTERS.createdIn &&
        !_.isEmpty(this.selectedCreationSites) &&
        this.hasSelectedForeignCreationSite
      )
    },

    sortOptions() {
      return _.compact([
        this.searchTerms
          ? {
              id: SORT_OPTIONS.coincidence,
              title: this.$t('common.coincidence'),
              action: () => this.handleSortChange(SORT_OPTIONS.coincidence),
            }
          : null,
        {
          id: SORT_OPTIONS.newestFirst,
          title: this.$t('filters.date.creation_date_newest_first'),
          action: () => this.handleSortChange(SORT_OPTIONS.newestFirst),
        },
        {
          id: SORT_OPTIONS.oldestFirst,
          title: this.$t('filters.date.creation_date_oldest_first'),
          action: () => this.handleSortChange(SORT_OPTIONS.oldestFirst),
        },
        {
          title: this.$t('common.a_z'),
          id: SORT_OPTIONS.nameAsc,
          action: () => this.handleSortChange(SORT_OPTIONS.nameAsc),
        },
        {
          title: this.$t('common.z_a'),
          id: SORT_OPTIONS.nameDesc,
          action: () => this.handleSortChange(SORT_OPTIONS.nameDesc),
        },
      ])
    },

    selectedFiltersCount() {
      const count = Object.keys(this.selectedFilters).length
      return this.isRegularUser ? count - 1 : count
    },

    siteOptions() {
      const organizationSites = this.hasMultiSiteScope
        ? this.authorizedSiteDetails
        : this.organizationSites

      return buildSiteOptions({
        accountSiteId: this.account?.site?._id,
        organizationSites,
        t: (...args) => this.$t(...args),
      })
    },

    selectedAssignees() {
      return this.selectedFilters?.assignees ?? []
    },

    selectedTeams() {
      return this.selectedFilters?.assignedTeams ?? []
    },

    selectedAssignedSites() {
      return this.selectedFilters?.assignedSites ?? []
    },

    selectedCreationSites() {
      return this.selectedFilters?.sites ?? []
    },

    selectedExecutableSites() {
      return this.selectedFilters?.executableInSite ?? []
    },

    activeSitePickerConfig() {
      const configs = {
        [ADMIN_FILTERS.createdIn]: {
          gate: this.isCreatedInFilterForSiteAdminsEnabled,
          id: ADMIN_FILTERS.createdIn,
          testId: 'routineFilters-selectorFilter-createdIn',
          value: this.selectedCreationSites,
        },
        [REGULAR_USER_FILTERS.forMySite]: {
          gate: this.hasMultiSiteScope,
          id: REGULAR_USER_FILTERS.forMySite,
          testId: 'routineFilters-selectorFilter-assignedSites',
          value: this.selectedAssignedSites,
        },
        [ROUTINE_MANAGER_FILTERS.executableInSite]: {
          gate: this.hasMultiSiteScope,
          id: ROUTINE_MANAGER_FILTERS.executableInSite,
          testId: 'routineFilters-selectorFilter-executableInSite',
          value: this.selectedExecutableSites,
        },
      }

      const config = configs[this.adminActiveFilter]
      if (!config || !config.gate) return null
      return config
    },

    teamOptions() {
      return this.availableTeams.map(team => ({
        text: team.name,
        value: team.id,
      }))
    },

    adminFilters() {
      return [
        {
          text: this.$t('routines.my_routines'),
          value: ADMIN_FILTERS.myRoutines,
        },
        {
          text: this.$t('routines.assigned_to_user'),
          value: ADMIN_FILTERS.assignees,
        },
        {
          text: this.$t('routines.assigned_to_team'),
          value: REGULAR_USER_FILTERS.forMyTeams,
        },
        {
          text: this.$t('routines.assigned_to_site'),
          value: REGULAR_USER_FILTERS.forMySite,
        },
        {
          text: this.$t('routines.assigned_globally'),
          value: REGULAR_USER_FILTERS.isGloballyAssigned,
        },
        {
          text: this.$t('routines.created_in'),
          value: ADMIN_FILTERS.createdIn,
        },
        {
          text: this.$t('routines.executable_in'),
          value: ROUTINE_MANAGER_FILTERS.executableInSite,
        },
        {
          text: this.$t('common.all'),
          value: ADMIN_FILTERS.all,
        },
      ]
    },

    adminMyRoutineFilters() {
      return [
        {
          text: this.$t('routines.to_do'),
          value: ADMIN_FILTERS.toDo,
          apiValue: true,
        },
        {
          text: this.$t('routines.executable_in_site'),
          value: ROUTINE_MANAGER_FILTERS.executableInSite,
          apiValue: [getEntityId(this.account.site)],
        },
        {
          text: this.$t('routines.created_by_me'),
          value: ROUTINE_MANAGER_FILTERS.createdBy,
          apiValue: this.accountId,
        },
        {
          text: this.$t('common.roles.i_am_collaborator'),
          value: ROUTINE_MANAGER_FILTERS.collaborators,
          apiValue: [this.accountId],
        },
      ]
    },

    routineManagerFilters() {
      const routineManagerFilterOptions = [
        {
          text: this.$t('routines.for_me'),
          value: ADMIN_FILTERS.assignees,
          apiValue: [this.accountId],
        },
        {
          text: this.hasMultiSiteScope
            ? this.$t('routines.assigned_to_site')
            : this.$t('routines.for_my_site'),
          value: REGULAR_USER_FILTERS.forMySite,
          apiValue: this.defaultSiteFilterValue,
        },
        {
          text: this.$t('routines.assigned_globally'),
          value: REGULAR_USER_FILTERS.isGloballyAssigned,
          apiValue: true,
        },
        {
          text: this.hasMultiSiteScope
            ? this.$t('routines.executable_in')
            : this.$t('routines.executable_in_site'),
          value: ROUTINE_MANAGER_FILTERS.executableInSite,
          apiValue: this.defaultSiteFilterValue,
        },
        {
          text: this.$t('routines.created_by_me'),
          value: ROUTINE_MANAGER_FILTERS.createdBy,
          apiValue: this.accountId,
        },
        {
          text: this.$t('common.roles.i_am_collaborator'),
          value: ROUTINE_MANAGER_FILTERS.collaborators,
          apiValue: [this.accountId],
        },
      ]

      if (this.isCreatedInFilterForSiteAdminsEnabled) {
        const createdInIndex = routineManagerFilterOptions.findIndex(
          option => option.value === ROUTINE_MANAGER_FILTERS.createdBy
        )
        const insertAt =
          createdInIndex === -1
            ? routineManagerFilterOptions.length
            : createdInIndex
        routineManagerFilterOptions.splice(insertAt, 0, {
          text: this.$t('routines.created_in'),
          value: ADMIN_FILTERS.createdIn,
        })
      }

      if (this.showForMyTeamOption) {
        routineManagerFilterOptions.splice(1, 0, {
          text: this.$t('routines.for_my_teams'),
          value: REGULAR_USER_FILTERS.forMyTeams,
          apiValue: [...this.userTeams],
        })
      }
      return routineManagerFilterOptions
    },

    regularUserFilters() {
      const regularUserChips = [
        {
          text: this.$t('routines.for_me'),
          value: ADMIN_FILTERS.assignees,
          apiValue: [this.accountId],
        },
        {
          text: this.$t('routines.for_my_site'),
          value: REGULAR_USER_FILTERS.forMySite,
          apiValue: this.defaultSiteFilterValue,
        },
        {
          text: this.$t('routines.assigned_globally'),
          value: REGULAR_USER_FILTERS.isGloballyAssigned,
          apiValue: true,
        },
      ]

      if (this.showForMyTeamOption) {
        regularUserChips.splice(1, 0, {
          text: this.$t('routines.for_my_teams'),
          value: REGULAR_USER_FILTERS.forMyTeams,
          apiValue: [...this.userTeams],
        })
      }
      return regularUserChips
    },

    isOrganizationAdminOrReverAdmin() {
      return this.account.isAdmin || this.account.isOrganizationAdmin
    },

    isRoutineManagerOrSiteAdmin() {
      return (
        !this.isOrganizationAdminOrReverAdmin &&
        (this.account.isSiteAdmin || this.account.isRoutineManager)
      )
    },

    isRegularUser() {
      return (
        !this.isOrganizationAdminOrReverAdmin &&
        !this.isRoutineManagerOrSiteAdmin
      )
    },

    availableFiltersByRole() {
      const defaultFilters = [
        'activeRoutine',
        'categories',
        'routineType',
        'situationTypes',
        'tags',
      ]
      if (this.isRoutineManagerOrSiteAdmin) return [...defaultFilters, 'sites']
      return defaultFilters
    },

    viewOptions() {
      return [
        {
          title: this.$t('common.view_as_cards'),
          id: VIEW_OPTIONS.cards,
          testId: 'explore-viewOption-cards',
          action: () => this.$emit('viewSelected', VIEW_OPTIONS.cards),
        },
        {
          title: this.$t('common.view_as_list'),
          id: VIEW_OPTIONS.table,
          testId: 'explore-viewOption-list',
          action: () => this.$emit('viewSelected', VIEW_OPTIONS.table),
        },
      ]
    },

    userTeams() {
      return this.availableTeams
        .filter(team => this.account.teams.includes(team.id))
        .map(team => team.id)
    },

    showForMyTeamOption() {
      return this.selectedTeams.length > 0 || this.userTeams.length > 0
    },
  },

  methods: {
    applyIfRequested({ source, shouldApply }) {
      if (source === 'user' && shouldApply) {
        this.applyFilters()
      }
    },

    async initFilters() {
      await this.searchTeams()
      this.setFiltersFromQuery()

      this.applyFilters()
    },

    clearOrgAdminFilters() {
      this.resetGeneralFilters()

      this.handleMainSelectorFilterChange({ value: ADMIN_FILTERS.myRoutines })

      this.applyFilters()
    },

    clearFilters() {
      this.resetGeneralFilters()
      this.applyFilters()
    },

    handleClearAssignees() {
      this.handleAdminFilterChange({
        key: 'assignees',
        value: [],
        source: 'user',
        shouldApply: true,
      })
    },

    handleAdminAssigneeFilterChange(payload) {
      const hasValue = _.has(payload, 'value')
      const nextPayload = hasValue ? payload : {}

      this.handleAdminFilterChange({
        key: 'assignees',
        ...nextPayload,
        value: nextPayload ? nextPayload.value : payload,
      })
    },

    resetGeneralFilters() {
      this.handleGeneralFiltersChange({ serializedFilters: {} })
      this.initialFilters = {}

      this.trackRoutineList({ action: 'resetFilters' })
    },

    setFiltersFromQuery() {
      const filtersToOmit = ['currentPage', 'searchTerms']

      if (this.isOrganizationAdminOrReverAdmin) {
        filtersToOmit.push('isGloballyAssigned')
      }

      const query = _.omit(this.$route.query, filtersToOmit)

      this.generalFilters = { ...this.getGeneralFilters(query) }
      this.initialFilters = { ...this.generalFilters }
      this.filterBySelection = { ...this.getSelectorFilters(query) }

      this.handleSelectedFiltersChange()
    },

    getGeneralFilters(query) {
      const generalFilters = {}

      const routineManagerFilterValues = Object.values(ROUTINE_MANAGER_FILTERS)
      const adminFilterValues = Object.values(ADMIN_FILTERS)
      const regularUserFilterValues = Object.values(REGULAR_USER_FILTERS)

      for (const key in query) {
        const isSelectorFilter =
          routineManagerFilterValues.includes(key) ||
          adminFilterValues.includes(key) ||
          regularUserFilterValues.includes(key)

        if (!isSelectorFilter) {
          if (key === 'isActive') {
            generalFilters.isActive = query[key] === 'true' || query[key] === true
            continue
          }

          generalFilters[key] = _.isArray(query[key])
            ? query[key]
            : [query[key]]
        }
      }

      return generalFilters
    },

    getSelectorFilters(query) {
      const filterBySelection = {}

      const generalFilters = [...this.availableFiltersByRole, 'type', 'isActive']

      const keysToOmitForSelectorFilters = this
        .isCreatedInFilterForSiteAdminsEnabled
        ? generalFilters.filter(key => key !== ADMIN_FILTERS.createdIn)
        : generalFilters

      const filterBySelectionQuery = _.omit(query, keysToOmitForSelectorFilters)

      for (const key in filterBySelectionQuery) {
        filterBySelection[key] =
          _.isArray(filterBySelectionQuery[key]) ||
          key === ROUTINE_MANAGER_FILTERS.createdBy ||
          key === REGULAR_USER_FILTERS.isGloballyAssigned
            ? filterBySelectionQuery[key]
            : [filterBySelectionQuery[key]]

        this.handleMainSelectorFilterChange({ value: key })
      }

      Object.assign(filterBySelection, this.filterBySelection)

      if (_.isEmpty(filterBySelection)) {
        const filtersToSetByDefault = this.isOrganizationAdminOrReverAdmin
          ? { [ADMIN_FILTERS.toDo]: true, isGloballyAssigned: false }
          : {
              [ADMIN_FILTERS.assignees]: [this.accountId],
            }

        this.selectedFilters = filtersToSetByDefault
        this.adminActiveFilter = this.isOrganizationAdminOrReverAdmin
          ? ADMIN_FILTERS.myRoutines
          : null
        this.activeFilter = this.isOrganizationAdminOrReverAdmin
          ? ADMIN_FILTERS.toDo
          : ADMIN_FILTERS.assignees
        return filtersToSetByDefault
      }

      return filterBySelection
    },

    handleChipFilterChange(filter) {
      this.activeFilter = filter
      this.handleMainSelectorFilterChange({ value: filter })
      this.applyFilters()
    },

    applyFilters() {
      this.$emit('change', this.selectedFilters)
    },

    getDataAsArray(data) {
      if (_.isEmpty(data)) {
        return []
      }
      return _.isArray(data) ? data : [data]
    },

    handleMainSelectorFilterChange({ value, source, shouldApply }) {
      this.cleanSelectedFilters(value)

      const setMyRoutines = () => {
        this.activeFilter = ADMIN_FILTERS.toDo
        this.adminActiveFilter = ADMIN_FILTERS.myRoutines
        this.filterBySelection = {
          [ADMIN_FILTERS.toDo]: true,
          isGloballyAssigned: false,
        }
      }

      const setAssignedTeams = () => {
        this.activeFilter = value
        if (this.isOrganizationAdminOrReverAdmin) {
          this.adminActiveFilter = REGULAR_USER_FILTERS.forMyTeams
        } else {
          this.adminActiveFilter = value
        }
        const queryAssignedTeams = this.getDataAsArray(
          this.$route?.query?.assignedTeams
        )
        const defaultTeams = this.userTeams
        const assignedTeams = _.isEmpty(this.selectedTeams)
          ? defaultTeams
          : this.selectedTeams

        this.filterBySelection = {
          [REGULAR_USER_FILTERS.forMyTeams]: _.isEmpty(queryAssignedTeams)
            ? assignedTeams
            : queryAssignedTeams,
        }
      }

      const setExecutableInSite = () => {
        this.adminActiveFilter = ROUTINE_MANAGER_FILTERS.executableInSite
        this.activeFilter = ROUTINE_MANAGER_FILTERS.executableInSite
        const queryExecutableInSite = this.getDataAsArray(
          this.$route?.query?.executableInSite
        )
        const defaultExecutableInSite = this.defaultSiteFilterValue
        const executableInSiteData = _.isEmpty(this.selectedExecutableSites)
          ? defaultExecutableInSite
          : this.selectedExecutableSites
        this.filterBySelection = {
          [ROUTINE_MANAGER_FILTERS.executableInSite]: _.isEmpty(
            queryExecutableInSite
          )
            ? executableInSiteData
            : queryExecutableInSite,
        }
      }

      const setAssignedSites = () => {
        this.adminActiveFilter = REGULAR_USER_FILTERS.forMySite
        this.activeFilter = value
        const queryAssignedSites = this.getDataAsArray(
          this.$route?.query?.assignedSites
        )
        const defaultAssignedSites = this.defaultSiteFilterValue
        const assignedSitesData = _.isEmpty(this.selectedAssignedSites)
          ? defaultAssignedSites
          : this.selectedAssignedSites
        this.filterBySelection = {
          [REGULAR_USER_FILTERS.forMySite]: _.isEmpty(queryAssignedSites)
            ? assignedSitesData
            : queryAssignedSites,
        }
      }

      const setGloballyAssigned = () => {
        this.activeFilter = REGULAR_USER_FILTERS.isGloballyAssigned
        this.adminActiveFilter = REGULAR_USER_FILTERS.isGloballyAssigned
        this.filterBySelection = {
          [REGULAR_USER_FILTERS.isGloballyAssigned]: true,
        }
      }

      const setAssignees = () => {
        this.adminActiveFilter = ADMIN_FILTERS.assignees
        this.activeFilter = value
        const queryAssignees = this.getDataAsArray(
          this.$route?.query?.assignees
        )
        this.filterBySelection = {
          [ADMIN_FILTERS.assignees]: _.isEmpty(queryAssignees)
            ? [this.accountId]
            : queryAssignees,
        }
      }

      const setCreatedBy = () => {
        this.activeFilter = ROUTINE_MANAGER_FILTERS.createdBy
        this.adminActiveFilter = ADMIN_FILTERS.myRoutines
        this.filterBySelection = {
          [ROUTINE_MANAGER_FILTERS.createdBy]:
            this.$route?.query?.createdBy || this.accountId,
        }
      }

      const setCreatedIn = () => {
        this.activeFilter = ADMIN_FILTERS.createdIn
        this.adminActiveFilter = ADMIN_FILTERS.createdIn
        const queryCreatedIn = this.getDataAsArray(this.$route?.query?.sites)
        const defaultCreatedInSite = [getEntityId(this.account.site)]
        const createdInSiteData = _.isEmpty(this.selectedCreationSites)
          ? defaultCreatedInSite
          : this.selectedCreationSites

        this.filterBySelection = {
          [ADMIN_FILTERS.createdIn]: _.isEmpty(queryCreatedIn)
            ? createdInSiteData
            : queryCreatedIn,
        }
      }

      const setCollaborators = () => {
        this.activeFilter = ROUTINE_MANAGER_FILTERS.collaborators
        this.adminActiveFilter = ROUTINE_MANAGER_FILTERS.collaborators
        const queryCollaborators = this.getDataAsArray(
          this.$route?.query?.collaborators
        )
        const defaultCollaborators = [this.accountId]
        this.filterBySelection = {
          [ROUTINE_MANAGER_FILTERS.collaborators]: _.isEmpty(queryCollaborators)
            ? defaultCollaborators
            : queryCollaborators,
        }
      }

      const setAll = () => {
        this.activeFilter = null
        this.adminActiveFilter = ADMIN_FILTERS.all
        this.filterBySelection = {}
      }

      switch (value) {
        case ADMIN_FILTERS.myRoutines:
          setMyRoutines()
          break
        case REGULAR_USER_FILTERS.forMyTeams:
          setAssignedTeams()
          break
        case ROUTINE_MANAGER_FILTERS.executableInSite:
          setExecutableInSite()
          break
        case ADMIN_FILTERS.toDo:
          setMyRoutines()
          break
        case REGULAR_USER_FILTERS.forMySite:
          setAssignedSites()
          break
        case REGULAR_USER_FILTERS.isGloballyAssigned:
          setGloballyAssigned()
          break
        case ADMIN_FILTERS.assignees:
          setAssignees()
          break
        case ROUTINE_MANAGER_FILTERS.createdBy:
          setCreatedBy()
          break
        case ADMIN_FILTERS.createdIn:
          setCreatedIn()
          break
        case ROUTINE_MANAGER_FILTERS.collaborators:
          setCollaborators()
          break
        case ADMIN_FILTERS.all:
          setAll()
          break
        default:
          break
      }

      this.handleSelectedFiltersChange()

      this.applyIfRequested({ source, shouldApply })
    },

    cleanSelectedFilters(selectedFilter) {
      Object.values({ ...ROUTINE_MANAGER_FILTERS, ...ADMIN_FILTERS }).forEach(
        value => {
          if (value !== selectedFilter) {
            delete this.selectedFilters[value]
            delete this.filterBySelection[value]
          }
        }
      )
    },

    handleAdminFilterChange({ key, value, source, shouldApply }) {
      const SITE_FILTER_KEYS = [
        REGULAR_USER_FILTERS.forMySite,
        ROUTINE_MANAGER_FILTERS.executableInSite,
        ADMIN_FILTERS.createdIn,
      ]

      const isDeferredDraft = source === 'user' && shouldApply === false
      const resolvedValue =
        !isDeferredDraft &&
        SITE_FILTER_KEYS.includes(key) &&
        _.isEmpty(value)
          ? this.defaultSiteFilterValue
          : value

      this.filterBySelection = { [key]: resolvedValue }
      this.handleSelectedFiltersChange()

      this.applyIfRequested({ source, shouldApply })
    },

    async searchTeams(terms = '') {
      try {
        this.isSearchingTeams = true
        const response = await api.teams.getTeamsStream(this.organizationId, {
          limit: 30,
          terms,
          sort: {
            by: terms.length ? '_score' : 'name',
            order: terms.length ? 'desc' : 'asc',
          },
        })
        this.availableTeams =
          response?.data?.results.map(result => result.team) ?? []

        const userTeamsExists = this.availableTeams.some(team =>
          this.account.teams.includes(team.id)
        )

        if (!userTeamsExists) {
          try {
            const [userAsMember, userAsLeader] = await Promise.all([
              api.teams.getTeamsStream(this.organizationId, {
                limit: 30,
                filters: {
                  members: this.accountId,
                },
              }),
              api.teams.getTeamsStream(this.organizationId, {
                limit: 30,
                filters: {
                  leaders: [this.accountId],
                },
                sort: {
                  by: terms.length ? '_score' : 'name',
                  order: terms.length ? 'desc' : 'asc',
                },
              }),
            ])

            this.availableTeams = _.uniqBy(
              [
                ...this.availableTeams,
                ...(userAsMember?.data?.results ?? []).map(
                  result => result.team
                ),
                ...(userAsLeader?.data?.results ?? []).map(
                  result => result.team
                ),
              ],
              'id'
            )
          } catch (error) {
            this.showError(error)
          }
        }
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isSearchingTeams = false
      }
    },

    async handleSearchAccounts(terms = '') {
      try {
        this.isSearchingAccounts = true
        const filters = { isDeleted: false }
        if (_.isEmpty(terms)) {
          filters.site = getEntityId(this.account.site)
        }
        const assignees = await this.fetchAccounts(filters, terms)

        let initialAssignees = []

        const filterSelectedAssignees = this.selectedAssignees.filter(
          assigneeId => this.accountId !== assigneeId
        )

        if (!this.filtersInitialized && filterSelectedAssignees.length) {
          initialAssignees = await this.fetchAccounts(
            { filters, id: this.selectedAssignees },
            terms
          )
        }

        this.accounts = [this.account, ...initialAssignees, ...assignees]
      } catch (err) {
        this.showError(err)
      } finally {
        this.isSearchingAccounts = false
        this.filtersInitialized = true
      }
    },

    async fetchAccounts(filters, terms) {
      const response = await api.account.getAccounts(this.organizationId, {
        filters,
        terms,
      })
      return response?.data?.results ?? []
    },

    handleMyRoutineFilterChange(filter, fallbackShouldApply = true) {
      const value = filter?.value ?? filter
      const shouldApply = filter?.shouldApply ?? fallbackShouldApply
      this.activeFilter = value
      this.cleanSelectedFilters(value)

      this.filterBySelection[value] = _.find(this.adminMyRoutineFilters, {
        value,
      }).apiValue

      if (value === ADMIN_FILTERS.toDo) {
        this.filterBySelection.isGloballyAssigned = false
      } else {
        delete this.filterBySelection.isGloballyAssigned
      }

      this.handleSelectedFiltersChange()

      this.applyIfRequested({ source: filter?.source ?? 'user', shouldApply })
    },

    handleGeneralFiltersChange(selectedFilters) {
      this.generalFilters = { ...selectedFilters.serializedFilters }

      this.handleSelectedFiltersChange()

      if (_.isEmpty(selectedFilters.serializedFilters)) {
        this.applyFilters()
      }
    },

    handleSelectedFiltersChange() {
      this.selectedFilters = {
        ...this.generalFilters,
        ...this.filterBySelection,
      }
    },

    handleSortChange(sort) {
      this.selectedSort = sort
      this.$emit('sortChange', {
        by: this.selectedSort.split('-')[0],
        order: this.selectedSort.split('-')[1],
      })
    },
  },
}
</script>
<style lang="scss">
.reset-filters-button:hover {
  opacity: 0.7;
}
</style>
